'use client';

import { useState, useEffect, useCallback } from 'react';
import { adminKycApi } from '../api';
import { KYCQuestion, CreateKYCQuestionInput, UpdateKYCQuestionInput } from '../types';

export function useKYCQuestions(tenantSlug: string) {
  const [data, setData] = useState<KYCQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchQuestions = useCallback(async () => {
    if (!tenantSlug) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminKycApi.getQuestions(tenantSlug);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch KYC questions');
    } finally {
      setIsLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return { data, isLoading, error, refetch: fetchQuestions };
}

export function useCreateKYCQuestion() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createQuestion = async (input: CreateKYCQuestionInput): Promise<KYCQuestion> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminKycApi.createQuestion(input);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create KYC question';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return { createQuestion, isLoading, error };
}

export function useUpdateKYCQuestion() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateQuestion = async (id: string, input: UpdateKYCQuestionInput): Promise<KYCQuestion> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminKycApi.updateQuestion(id, input);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update KYC question';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return { updateQuestion, isLoading, error };
}

export function useDeleteKYCQuestion() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteQuestion = async (id: string, tenantSlug: string): Promise<any> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await adminKycApi.deleteQuestion(id, tenantSlug);
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete KYC question';
      setError(msg);
      throw new Error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return { deleteQuestion, isLoading, error };
}
